import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { SUPPORTED_LANGUAGE_OPTIONS, type SupportedLanguage } from '@/i18n/supportedLanguages';
import { storeLanguage } from '@/lib/languageStorage';
import { cn } from '@/lib/utils';

const LANGUAGE_SHORT_LABEL: Record<SupportedLanguage, string> = {
  en: 'EN',
  pl: 'PL',
};

interface LanguageSwitcherProps {
  tone?: 'light' | 'dark';
  className?: string;
  onLanguageChange?: (language: SupportedLanguage) => void;
}

export function LanguageSwitcher({ tone = 'light', className, onLanguageChange }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const activeLanguage =
    i18n.resolvedLanguage === undefined ? i18n.language : i18n.resolvedLanguage;

  async function selectLanguage(language: SupportedLanguage) {
    if (activeLanguage === language) {
      return;
    }
    await i18n.changeLanguage(language);
    storeLanguage(language);
    await queryClient.invalidateQueries();
    if (onLanguageChange !== undefined) {
      onLanguageChange(language);
    }
  }

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 p-1 rounded-lg',
        tone === 'dark' ? 'bg-white/10' : 'bg-zoovpalelavender',
        className
      )}
    >
      {SUPPORTED_LANGUAGE_OPTIONS.map(function renderLanguageOption(language) {
        const isActive = activeLanguage === language;
        const activeClassName =
          tone === 'dark' ? 'bg-zoowhite text-zooindigo' : 'bg-zoowhite text-zooprimary shadow-sm';
        const inactiveClassName =
          tone === 'dark' ? 'text-white/70 hover:text-zoowhite' : 'text-zoomuted hover:text-zooindigo';
        return (
          <button
            key={language}
            type="button"
            aria-pressed={isActive}
            onClick={function chooseLanguage() {
              void selectLanguage(language);
            }}
            className={cn(
              'px-2.5 py-1.5 rounded-md text-xs font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-zooprimary',
              isActive ? activeClassName : inactiveClassName
            )}
          >
            {LANGUAGE_SHORT_LABEL[language]}
          </button>
        );
      })}
    </div>
  );
}
